// The one form every format is read into and written out of, so a conversion is a read and a write and never a pair of
// formats that know about each other.
export type ConfigValue = null | boolean | number | bigint | string | ConfigValue[] | { [key: string]: ConfigValue };

export type ReadResult = { ok: true; value: ConfigValue } | { ok: false; error: ReadError };

// Where the text stopped making sense, for the parsers that say; JSON's own is the one that often does not.
export interface ReadError {
  message: string;
  line?: number;
  column?: number;
}

// A write either produces the document or says why the format cannot hold what it was handed. `lost` is the rest of
// that answer: the paths it had no place for, which is TOML's nulls and the flat formats' empty containers.
export type WriteResult = { ok: true; text: string; lost: string[] } | { ok: false; message: string };

export interface WriteOptions {
  indent: number;
}

export const readValue = (value: ConfigValue): ReadResult => ({ ok: true, value });

export const unreadable = (message: string, at?: { line: number; column: number }): ReadResult => ({
  ok: false,
  error: { message, ...at },
});

export const written = (text: string, lost: string[] = []): WriteResult => ({ ok: true, text, lost });

export const unwritable = (message: string): WriteResult => ({ ok: false, message });

export function isRecord(value: ConfigValue): value is { [key: string]: ConfigValue } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// A flat format holds text and nothing else, so this is the whole of how a value gets its type back out of one.
export function readScalar(text: string): ConfigValue {
  if (text === "true") return true;
  if (text === "false") return false;
  if (text === "null") return null;
  // Read as a bigint first, so an integer is held exactly however long it is and never rounded on the way in.
  if (INTEGER.test(text)) return exactInteger(BigInt(text));
  // An exact round trip only, so `1.0`, `007` and `1e3` stay the strings they were written as, and the regex is what
  // keeps `Infinity` and a run of spaces from being read as numbers JSON could not hold anyway.
  return NUMERIC.test(text) && String(Number(text)) === text ? Number(text) : text;
}

// A double holds every integer below 2^53 and only some past it, so past it is where an integer is kept as a bigint.
export function exactInteger(value: bigint): number | bigint {
  return value >= -MAX_SAFE && value <= MAX_SAFE ? Number(value) : value;
}

export function writeScalar(value: ConfigValue): string {
  return value === null ? "null" : String(value);
}

// True where the bare text would not come back as the string it is, which is what a flat format wants quoting for.
export function ambiguousAsText(text: string): boolean {
  return readScalar(text) !== text;
}

// What a value is called in the one message that has to say why a format cannot hold the whole document.
export function describe(value: ConfigValue): string {
  if (Array.isArray(value)) return "a list";
  if (value === null) return "null";
  if (typeof value === "bigint") return "a single number";
  return typeof value === "string" ? "a single string" : `a single ${typeof value}`;
}

const NUMERIC = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/;

// `-0` is left out, being an integer that does not spell itself back.
const INTEGER = /^(?:0|-?[1-9]\d*)$/;

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
